import { useCallback, useEffect, useRef, useState } from "react";
import { isEqual } from "radash";

type Mode = "normal" | "quick";
type Patch<T> = Partial<T>;

interface QueueOptions<T> {
  defaultDelayMs: number; // for normal mode
  quickDelayMs?: number;
  shouldSkip?: () => boolean;
  preprocessPatch?: (patch: Patch<T>) => Patch<T>;
  isEqual?: (a: any, b: any) => boolean;
  processor: (args: {
    patch: Patch<T>;        // patch of changes being processed in this run
    originalItem: T;        // item at start()
    processedItem: T;       // last committed/processed snapshot
    processingItem: T;      // processedItem + patch (in-flight candidate)
  }) => Promise<void>;
}

function defaultIsEqual(a: any, b: any) {
  return isEqual(a, b);
}

function clone<T>(value: T): T {
  if (typeof (globalThis as any).structuredClone === "function") {
    return (globalThis as any).structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

export function useItemChangesQueue<T extends Record<string, any>>(opts: QueueOptions<T>) {
  const {
    defaultDelayMs,
    quickDelayMs = 50,
    processor,
    shouldSkip,
    preprocessPatch,
    isEqual = defaultIsEqual,
  } = opts;

  const [isProcessing, setIsProcessing] = useState(false); // Used by UI
  const [isStarted, setIsStarted] = useState(false);

  const originalRef = useRef<T | null>(null);
  const processedRef = useRef<T | null>(null);
  const processingRef = useRef<T | null>(null);

  const queuedPatchRef = useRef<Patch<T>>({});
  const queuedModeRef = useRef<Mode>("normal");

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runningRef = useRef(false);
  const flushRequestedRef = useRef(false);
  const runOnceRef = useRef<(force?: boolean) => Promise<void>>(async () => {});
  const mountedRef = useRef(true);

  const stopTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const isRunning = useCallback(() => {
    return processedRef.current !== null && originalRef.current !== null;
  }, []);

  const hasQueued = useCallback(() => {
    return Object.keys(queuedPatchRef.current).length > 0;
  }, []);

  const computeDelay = useCallback(() => {
    return queuedModeRef.current === "quick" ? quickDelayMs : defaultDelayMs;
  }, [defaultDelayMs, quickDelayMs]);

  const schedule = useCallback((force = false) => {
    if (!isRunning()) return;
    if (runningRef.current) return;
    if (!hasQueued()) return;

    if (!force && shouldSkip?.()) return;

    const delay = computeDelay();
    stopTimer();

    timerRef.current = setTimeout(() => {
      void runOnceRef.current(force);
    }, delay);
  }, [computeDelay, hasQueued, isRunning, shouldSkip, stopTimer]);

  const runOnce = useCallback(async (force = false) => {
    if (!isRunning()) return;
    if (runningRef.current) return;

    if (!force && !hasQueued()) return;
    if (!force && shouldSkip?.()) return;

    // no timer involvement anymore
    stopTimer();

    runningRef.current = true;
    if (mountedRef.current) setIsProcessing(true);

    const processedItem = processedRef.current!;
    const originalItem = originalRef.current!;

    // Drain queue
    const patch = clone(queuedPatchRef.current);
    queuedPatchRef.current = {};
    // Reset mode since queue is drained; new enqueues can escalate again
    queuedModeRef.current = "normal";

    const processingItem = clone(processedItem);
    Object.assign(processingItem, patch);
    processingRef.current = processingItem;

    try {
      await processor({ patch, originalItem, processedItem, processingItem });
      if (isRunning()) processedRef.current = processingItem;
    } catch (e) {
      // re-queue on failure
      if (isRunning()) queuedPatchRef.current = { ...patch, ...queuedPatchRef.current };
    } finally {
      runningRef.current = false;
      processingRef.current = null;
      if (mountedRef.current) setIsProcessing(false);

      if (hasQueued()) {
        if (flushRequestedRef.current) {
          flushRequestedRef.current = false;
          // Run next batch right away (0ms)
          stopTimer();
          timerRef.current = setTimeout(() => void runOnceRef.current(false), 0);
        } else {
          schedule(false); // normal/quick debounce behavior
        }
      } else {
        flushRequestedRef.current = false;
      }
    }
  }, [hasQueued, isRunning, processor, schedule, shouldSkip, stopTimer]);

  // keep ref updated
  useEffect(() => {
    runOnceRef.current = runOnce;
  }, [runOnce]);

  const mergeIntoQueue = useCallback(
    (incomingPatch: Patch<T>, mode: Mode = "normal") => {
      if (!isRunning()) return false;

      // Escalate only (never downgrade)
      if (mode === "quick") queuedModeRef.current = "quick";

      let patch = incomingPatch;
      if (preprocessPatch) patch = preprocessPatch(patch);

      const processed = processedRef.current!;
      const processing = processingRef.current;

      for (const key of Object.keys(patch)) {
        const nextVal = (patch as any)[key];

        const equalsProcessed = isEqual((processed as any)[key], nextVal);
        const equalsProcessing = processing ? isEqual((processing as any)[key], nextVal) : false;

        if (equalsProcessed || equalsProcessing) {
          delete (queuedPatchRef.current as any)[key];
        } else {
          (queuedPatchRef.current as any)[key] = nextVal;
        }
      }

      return Object.keys(queuedPatchRef.current).length > 0;
    },
    [isEqual, isRunning, preprocessPatch]
  );

  const enqueueChange = useCallback(
    (incomingPatch: Patch<T>, mode: Mode = "normal") => {
      const anyQueued = mergeIntoQueue(incomingPatch, mode);
      if (!anyQueued) return false;

      // If processor is not running
      if (!runningRef.current) schedule(false);

      return true;
    },
    [mergeIntoQueue, schedule]
  );

  const start = useCallback((item: T) => {
    stopTimer();
    setIsStarted(true);
    originalRef.current = item;
    processedRef.current = clone(item);
    processingRef.current = null;
    queuedPatchRef.current = {};
    queuedModeRef.current = "normal";
    runningRef.current = false;
    flushRequestedRef.current = false;
    if (mountedRef.current) setIsProcessing(false);
  }, [stopTimer]);

  const stop = useCallback(() => {
    stopTimer();
    const lastProcessedItem = processedRef.current;
    const lastQueuedPatch = queuedPatchRef.current;
    setIsStarted(false);
    originalRef.current = null;
    processedRef.current = null;
    processingRef.current = null;
    queuedPatchRef.current = {};
    queuedModeRef.current = "normal";
    runningRef.current = false;
    flushRequestedRef.current = false;
    if (mountedRef.current) setIsProcessing(false);

    return { lastProcessedItem, lastQueuedPatch };
  }, [stopTimer]);

  const updateProcessed = useCallback((attributeName: string, value: any) => {
    const current = processedRef.current;
    if (!current) return;

    const next = clone(current);
    (next as any)[attributeName] = value;

    processedRef.current = next;
  }, []);

  const flush = useCallback(() => {
    if (runningRef.current) {
      flushRequestedRef.current = true;
      return;
    }
    void runOnceRef.current(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopTimer();
    };
  }, [stopTimer]);

  return {
    isProcessing,
    isStarted,
    start,
    stop,
    enqueueChange,
    flush,
    updateProcessed,
    getProcessed: () => processedRef.current,
    getProcessing: () => processingRef.current,
    getQueuedPatch: () => queuedPatchRef.current,
    getQueuedMode: () => queuedModeRef.current,
    isProcessorRunning: () => runningRef.current,
  };
}
